import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import Loading from "../UI/Loading";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Paleta categórica validada (slots 1 e 2) e tinta neutra para marcas de referência.
export const SERIES_COLORS = {
  azul: "#2a78d6",
  laranja: "#eb6834",
  referencia: "#c3c2b7",
};

const TEXT_SECONDARY = "#52514e";
const GRID_COLOR = "rgba(0, 0, 0, 0.06)";

export const PERIODO_CINCO_DIAS = "cincoDias";
export const PERIODO_ANO_LETIVO = "anoLetivo";

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      position: "top",
      align: "start",
      labels: { usePointStyle: true, pointStyle: "rectRounded", boxWidth: 10, color: TEXT_SECONDARY },
    },
    tooltip: { padding: 10, boxPadding: 4 },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: TEXT_SECONDARY } },
    y: {
      beginAtZero: true,
      border: { display: false },
      grid: { color: GRID_COLOR },
      ticks: { color: TEXT_SECONDARY, precision: 0 },
    },
  },
};

const toDataset = ({ label, data, color }) => ({
  label,
  data,
  backgroundColor: color,
  borderRadius: 4,
  borderSkipped: "start",
  maxBarThickness: 28,
  categoryPercentage: 0.7,
  barPercentage: 0.9,
});

const StatTile = ({ label, value, caption }) => (
  <Box flex={1} minWidth={120}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={600}>
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    )}
  </Box>
);

/**
 * Cartão de gráfico de engajamento com seletor de período.
 * - fetchers: { [PERIODO_CINCO_DIAS]: fn, [PERIODO_ANO_LETIVO]: fn }
 * - getSeries(data): [{ label, data, color }]
 * - getStats(data): [{ label, value, caption }]
 */
const EngajamentoChart = ({ title, description, fetchers, getSeries, getStats, toolbar }) => {
  const [periodo, setPeriodo] = useState(PERIODO_CINCO_DIAS);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setHasError(false);
    fetchers[periodo]()
      .then((result) => {
        if (!active) return;
        if (!result || result.error) {
          setHasError(true);
          setData(null);
        } else {
          setData(result);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchers, periodo]);

  const labels = data?.listaAxeX ?? [];
  const series = data ? getSeries(data) : [];
  const stats = data && getStats ? getStats(data) : [];

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box height={260} display="flex" alignItems="center" justifyContent="center">
          <Loading size={28} description={`Carregando ${title}...`} />
        </Box>
      );
    }
    if (hasError || labels.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={8}>
          {hasError ? "Não foi possível carregar os dados." : "Sem dados para o período."}
        </Typography>
      );
    }
    return (
      <>
        {stats.length > 0 && (
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={2}>
            {stats.map((stat) => (
              <StatTile key={stat.label} {...stat} />
            ))}
          </Stack>
        )}
        <Box height={260}>
          <Bar
            data={{ labels, datasets: series.map(toDataset) }}
            options={chartOptions}
            aria-label={`Gráfico de barras: ${title}`}
            role="img"
          />
        </Box>
        <Box textAlign="right" mt={1}>
          <Button size="small" color="inherit" onClick={() => setShowTable((v) => !v)}>
            {showTable ? "Ocultar tabela" : "Ver tabela"}
          </Button>
        </Box>
        {showTable && (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" aria-label={`Dados de ${title}`}>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  {series.map((s) => (
                    <TableCell key={s.label} align="right">
                      {s.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {labels.map((label, i) => (
                  <TableRow key={label + i}>
                    <TableCell>{label}</TableCell>
                    {series.map((s) => (
                      <TableCell key={s.label} align="right">
                        {s.data[i] ?? "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </>
    );
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, height: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        mb={2}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {toolbar}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={periodo}
            onChange={(event, value) => value && setPeriodo(value)}
          >
            <ToggleButton value={PERIODO_CINCO_DIAS}>5 dias</ToggleButton>
            <ToggleButton value={PERIODO_ANO_LETIVO}>Ano letivo</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      {renderBody()}
    </Paper>
  );
};

export default EngajamentoChart;
